import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@ecoinvent/ui';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import useVersionInfo from 'hooks/useVersionInfo';
import { isV4Release } from 'utilities/constants/versions';

import {
  collectNodesToRemove,
  countsFromNodes,
  DerivedEdge,
  edgeId,
  expansionOwnerId,
  fetchNeighborhood,
  makeInputEdges,
  makeOutputEdges,
  MAX_NODES_PER_EXPANSION,
  mergeEdges,
  mergeNodes,
  NeighborhoodPageParams,
  NodeCounts,
  resolveExpansionInstances,
  ShownCounts,
} from './supplyChainUtils';
import { SupplyChainNeighborhood, SupplyChainNode, SupplyChainPage } from './types';

export type { DerivedEdge, NodeCounts, ShownCounts };

// Cached per node and reused by both directions, so neither limit can be trimmed.
const FIRST_PAGE_PARAMS: NeighborhoodPageParams = {
  inputs_offset: 0,
  inputs_limit: MAX_NODES_PER_EXPANSION,
  outputs_offset: 0,
  outputs_limit: MAX_NODES_PER_EXPANSION,
};

const pageParams = (direction: 'inputs' | 'outputs', offset: number): NeighborhoodPageParams =>
  direction === 'inputs'
    ? { inputs_offset: offset, inputs_limit: MAX_NODES_PER_EXPANSION, outputs_limit: 1 }
    : { outputs_offset: offset, outputs_limit: MAX_NODES_PER_EXPANSION, inputs_limit: 1 };

const ERROR_TOAST_TITLE = 'Oh no...';

// Chakra's toast `id` only dedupes when paired with `toast.isActive`.
const EXPAND_ERROR_TOAST = {
  id: 'supply-chain-expand-error',
  title: ERROR_TOAST_TITLE,
  description: 'There was an error expanding this activity. Please try again later.',
  status: 'error',
  isClosable: true,
} as const;

const SHOW_MORE_ERROR_TOAST = {
  id: 'supply-chain-show-more-error',
  title: ERROR_TOAST_TITLE,
  description: 'There was an error loading more activities. Please try again later.',
  status: 'error',
  isClosable: true,
} as const;

export const useSupplyChain = (datasetId?: string) => {
  const { ver: version, system_model } = useVersionInfo();
  const toast = useToast();

  const isV4 = isV4Release(version);

  const { data: initialNeighborhood, isLoading } = useQuery<SupplyChainNeighborhood, AxiosError>({
    queryKey: ['supply-chain', datasetId, version, system_model],
    queryFn: () => fetchNeighborhood(datasetId as string, version, system_model, FIRST_PAGE_PARAMS),
    enabled: isV4 && !!datasetId,
    retry: false,
  });

  const initialized = useRef(false);
  const neighborhoodCache = useRef<Record<string, SupplyChainNeighborhood>>({});
  const addedByExpansion = useRef<Record<string, Set<string>>>({});
  const edgesByExpansion = useRef<Record<string, Set<string>>>({});
  const nodesRef = useRef<SupplyChainNode[]>([]);
  const edgesRef = useRef<DerivedEdge[]>([]);
  // Next page offset; not `addedByExpansion[key].size`, which undercounts because duplicates add no card.
  const fetchedCounts = useRef<Record<string, number>>({});
  // `neighborhoodCache` only holds page 0, so its totals go stale.
  const serverTotals = useRef<Record<string, number>>({});
  const inFlightExpansions = useRef<Set<string>>(new Set());
  const partiallyCollapsed = useRef<Set<string>>(new Set());
  const generation = useRef(0);
  const collapseEpochs = useRef<Record<string, number>>({});

  const [nodes, setNodes] = useState<SupplyChainNode[]>([]);
  const [edges, setEdges] = useState<DerivedEdge[]>([]);
  const [nodeCounts, setNodeCounts] = useState<NodeCounts>({});
  const [shownCounts, setShownCounts] = useState<ShownCounts>({});
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  const [expandedOutputNodeIds, setExpandedOutputNodeIds] = useState<Set<string>>(new Set());
  const pendingFetches = useRef(0);
  const [isExpanding, setIsExpanding] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const rootNodeId = initialNeighborhood?.root.id ?? '';

  useEffect(() => {
    generation.current += 1;
    initialized.current = false;
    neighborhoodCache.current = {};
    addedByExpansion.current = {};
    edgesByExpansion.current = {};
    nodesRef.current = [];
    edgesRef.current = [];
    fetchedCounts.current = {};
    serverTotals.current = {};
    collapseEpochs.current = {};
    inFlightExpansions.current = new Set();
    partiallyCollapsed.current = new Set();
    setNodes([]);
    setEdges([]);
    setNodeCounts({});
    setShownCounts({});
    setExpandedNodeIds(new Set());
    setExpandedOutputNodeIds(new Set());
  }, [datasetId, version, system_model]);

  useEffect(() => {
    if (!initialNeighborhood || initialized.current) return;
    initialized.current = true;

    addedByExpansion.current = {};
    edgesByExpansion.current = {};
    fetchedCounts.current = {};
    serverTotals.current = {};
    collapseEpochs.current = {};
    inFlightExpansions.current = new Set();
    partiallyCollapsed.current = new Set();

    const { root, inputs, outputs } = initialNeighborhood;
    neighborhoodCache.current[root.id] = initialNeighborhood;

    const visibleInputs = inputs.results;
    const visibleOutputs = outputs.results;
    const initInputEdges = makeInputEdges(root.id, visibleInputs);
    const initOutputEdges = makeOutputEdges(root.id, visibleOutputs);

    addedByExpansion.current[`${root.id}_inputs`] = new Set(visibleInputs.map((n) => n.id));
    addedByExpansion.current[`${root.id}_outputs`] = new Set(visibleOutputs.map((n) => n.id));
    edgesByExpansion.current[`${root.id}_inputs`] = new Set(initInputEdges.map((e) => e.id));
    edgesByExpansion.current[`${root.id}_outputs`] = new Set(initOutputEdges.map((e) => e.id));
    fetchedCounts.current[`${root.id}_inputs`] = visibleInputs.length;
    fetchedCounts.current[`${root.id}_outputs`] = visibleOutputs.length;
    serverTotals.current[`${root.id}_inputs`] = inputs.total;
    serverTotals.current[`${root.id}_outputs`] = outputs.total;

    const initNodes = mergeNodes([root, ...visibleInputs], visibleOutputs);
    const initEdges = [...initInputEdges, ...initOutputEdges];
    nodesRef.current = initNodes;
    edgesRef.current = initEdges;
    setNodes(initNodes);
    setEdges(initEdges);
    // Root last, so its totals win if it is also one of its own inputs.
    setNodeCounts({
      ...countsFromNodes([...visibleInputs, ...visibleOutputs]),
      [root.id]: { inputs: inputs.total, outputs: outputs.total },
    });
    setShownCounts({ [root.id]: { inputs: visibleInputs.length, outputs: visibleOutputs.length } });
    setExpandedNodeIds(new Set([root.id]));
    setExpandedOutputNodeIds(new Set([root.id]));
  }, [initialNeighborhood, resetKey]);

  // Applying a page whose stamp changed in flight would re-add torn-down cards and desync the offsets.
  const expansionStamp = useCallback((key: string) => `${generation.current}:${collapseEpochs.current[key] ?? 0}`, []);

  // The ref updates synchronously, so two expansions resolving in the same tick read each other's cards.
  const setNodesSynced = useCallback((updater: (prev: SupplyChainNode[]) => SupplyChainNode[]) => {
    const next = updater(nodesRef.current);
    nodesRef.current = next;
    setNodes(next);
  }, []);

  const setEdgesSynced = useCallback((updater: (prev: DerivedEdge[]) => DerivedEdge[]) => {
    const next = updater(edgesRef.current);
    edgesRef.current = next;
    setEdges(next);
  }, []);

  const recordExpansion = useCallback((key: string, nodeIds: string[], edgeIds: string[]) => {
    partiallyCollapsed.current.delete(key);

    const nodeSet = addedByExpansion.current[key] ?? new Set<string>();
    nodeIds.forEach((id) => nodeSet.add(id));
    addedByExpansion.current[key] = nodeSet;

    const edgeSet = edgesByExpansion.current[key] ?? new Set<string>();
    edgeIds.forEach((id) => edgeSet.add(id));
    edgesByExpansion.current[key] = edgeSet;
  }, []);

  const isAncestorInExpansion = useCallback((candidateId: string, nodeId: string): boolean => {
    const visited = new Set<string>();
    const queue = [nodeId];
    while (queue.length > 0) {
      const current = queue.shift();
      if (current === undefined || visited.has(current)) continue;
      visited.add(current);
      for (const [key, nodeSet] of Object.entries(addedByExpansion.current)) {
        if (nodeSet.has(current)) {
          const parentId = expansionOwnerId(key);
          if (parentId === candidateId) return true;
          queue.push(parentId);
        }
      }
    }
    return false;
  }, []);

  const getDepth = useCallback((id: string): number => {
    for (const [key, nodeSet] of Object.entries(addedByExpansion.current)) {
      if (nodeSet.has(id)) {
        const parentId = expansionOwnerId(key);
        const direction = key.endsWith('_inputs') ? -1 : 1;
        return getDepth(parentId) + direction;
      }
    }
    return 0;
  }, []);

  const getOrFetchNeighborhood = useCallback(
    async (nodeId: string): Promise<SupplyChainNeighborhood | undefined> => {
      if (!isV4) return undefined;
      if (neighborhoodCache.current[nodeId]) return neighborhoodCache.current[nodeId];
      const gen = generation.current;
      try {
        const data = await fetchNeighborhood(nodeId, version, system_model, FIRST_PAGE_PARAMS);
        if (gen === generation.current) neighborhoodCache.current[nodeId] = data;
        return data;
      } catch {
        if (!toast.isActive(EXPAND_ERROR_TOAST.id)) toast(EXPAND_ERROR_TOAST);
        return undefined;
      }
    },
    [isV4, version, system_model, toast]
  );

  const applyPage = useCallback(
    (nodeId: string, direction: 'inputs' | 'outputs', page: SupplyChainPage) => {
      const key = `${nodeId}_${direction}`;
      const isInputs = direction === 'inputs';

      const { nodesToAdd, instances } = resolveExpansionInstances(
        nodeId,
        page.results,
        new Set(nodesRef.current.map((n) => n.id)),
        getDepth(nodeId) + (isInputs ? -1 : 1),
        (candidateId) => isAncestorInExpansion(candidateId, nodeId),
        getDepth
      );
      // `instances`, not `nodesToAdd`: cards already on canvas still need their edge.
      const pageEdges = isInputs ? makeInputEdges(nodeId, instances) : makeOutputEdges(nodeId, instances);

      recordExpansion(
        key,
        nodesToAdd.map((n) => n.id),
        pageEdges.map((e) => e.id)
      );
      // An empty page means the list shrank server-side; jump to `total` or the "+N more" pill goes inert.
      const fetched = page.results.length > 0 ? page.offset + page.results.length : page.total;
      fetchedCounts.current[key] = fetched;
      serverTotals.current[key] = page.total;
      const shown = Math.max(fetched, addedByExpansion.current[key]?.size ?? 0);

      setNodesSynced((prev) => mergeNodes(prev, nodesToAdd));
      setEdgesSynced((prev) => mergeEdges(prev, pageEdges));
      // `prev` wins so child seeding never overwrites a node's own counts.
      setNodeCounts((prev) => ({
        ...countsFromNodes(nodesToAdd),
        ...prev,
        // Spread, not `?? 0`: the other direction may be unknown, and `0` deletes its pill.
        [nodeId]: { ...prev[nodeId], ...(isInputs ? { inputs: page.total } : { outputs: page.total }) },
      }));
      setShownCounts((prev) => ({
        ...prev,
        [nodeId]: {
          inputs: isInputs ? shown : (prev[nodeId]?.inputs ?? 0),
          outputs: isInputs ? (prev[nodeId]?.outputs ?? 0) : shown,
        },
      }));
    },
    [getDepth, isAncestorInExpansion, recordExpansion, setEdgesSynced, setNodesSynced]
  );

  const expandNode = useCallback(
    async (nodeId: string) => {
      const key = `${nodeId}_inputs`;
      // Double-clicks pass the stale `expandedNodeIds` check; only the in-flight guard stops them.
      if (expandedNodeIds.has(nodeId) || inFlightExpansions.current.has(key)) return;
      inFlightExpansions.current.add(key);
      const stamp = expansionStamp(key);
      const datasetId = nodeId.split('#')[0];
      const needsFetch = !neighborhoodCache.current[datasetId];
      if (needsFetch) {
        pendingFetches.current += 1;
        setIsExpanding(true);
      }
      try {
        const neighborhood = await getOrFetchNeighborhood(datasetId);
        if (!neighborhood) return;
        if (stamp !== expansionStamp(key)) return;

        setNodeCounts((prev) => ({
          ...prev,
          [nodeId]: { inputs: neighborhood.inputs.total, outputs: neighborhood.outputs.total },
        }));
        applyPage(nodeId, 'inputs', neighborhood.inputs);
        setExpandedNodeIds((prev) => new Set([...prev, nodeId]));
      } finally {
        inFlightExpansions.current.delete(key);
        // In `finally`, or a throw leaves the full-canvas overlay up permanently.
        if (needsFetch) {
          pendingFetches.current -= 1;
          if (pendingFetches.current === 0) setIsExpanding(false);
        }
      }
    },
    [applyPage, expandedNodeIds, expansionStamp, getOrFetchNeighborhood]
  );

  const collapseExpansion = useCallback(
    (nodeId: string, direction: 'inputs' | 'outputs', activeNodeId?: string | null) => {
      const key = `${nodeId}_${direction}`;
      const expanded = direction === 'inputs' ? expandedNodeIds : expandedOutputNodeIds;
      if (!expanded.has(nodeId)) return;

      const directIds = [...(addedByExpansion.current[key] ?? new Set<string>())];
      const branch = collectNodesToRemove(directIds, addedByExpansion.current, new Set([nodeId]));

      const isSticky = (id: string) => id === activeNodeId || expandedNodeIds.has(id) || expandedOutputNodeIds.has(id);
      const keep = partiallyCollapsed.current.has(key)
        ? new Set<string>()
        : new Set(directIds.filter((id) => branch.has(id) && isSticky(id)));
      const toRemove = new Set([...branch].filter((id) => !keep.has(id)));

      const tornDownKeys = [key, ...Object.keys(addedByExpansion.current).filter((k) => branch.has(expansionOwnerId(k)))];
      const ownedEdgeIds = new Set(tornDownKeys.flatMap((k) => [...(edgesByExpansion.current[k] ?? [])]));

      tornDownKeys.forEach((k) => {
        delete addedByExpansion.current[k];
        delete edgesByExpansion.current[k];
        partiallyCollapsed.current.delete(k);
        collapseEpochs.current[k] = (collapseEpochs.current[k] ?? 0) + 1;
      });
      branch.forEach((id) => {
        (['inputs', 'outputs'] as const).forEach((dir) => {
          delete fetchedCounts.current[`${id}_${dir}`];
          delete serverTotals.current[`${id}_${dir}`];
          collapseEpochs.current[`${id}_${dir}`] = (collapseEpochs.current[`${id}_${dir}`] ?? 0) + 1;
        });
      });

      if (keep.size > 0) {
        addedByExpansion.current[key] = new Set(keep);
        edgesByExpansion.current[key] = new Set(
          [...keep].map((id) => (direction === 'inputs' ? edgeId(id, nodeId) : edgeId(nodeId, id)))
        );
        partiallyCollapsed.current.add(key);
        // Restart at 0: survivors need not be the first items, so resuming past them would strand earlier ones.
        fetchedCounts.current[key] = 0;
      } else {
        delete fetchedCounts.current[key];
        delete serverTotals.current[key];
      }

      const liveEdgeIds = new Set(Object.values(edgesByExpansion.current).flatMap((ids) => [...ids]));
      const removeEdgeIds = new Set<string>();
      edgesRef.current.forEach((e) => {
        if (toRemove.has(e.source_id) || toRemove.has(e.target_id)) removeEdgeIds.add(e.id);
      });
      ownedEdgeIds.forEach((id) => {
        if (!liveEdgeIds.has(id)) removeEdgeIds.add(id);
      });
      Object.values(edgesByExpansion.current).forEach((ids) => removeEdgeIds.forEach((id) => ids.delete(id)));

      setNodesSynced((prev) => prev.filter((n) => !toRemove.has(n.id)));
      setEdgesSynced((prev) => prev.filter((e) => !removeEdgeIds.has(e.id)));
      setShownCounts((prev) => {
        const next = { ...prev };
        toRemove.forEach((id) => delete next[id]);
        keep.forEach((id) => {
          next[id] = { inputs: 0, outputs: 0 };
        });
        next[nodeId] = {
          inputs: direction === 'inputs' ? keep.size : (prev[nodeId]?.inputs ?? 0),
          outputs: direction === 'outputs' ? keep.size : (prev[nodeId]?.outputs ?? 0),
        };
        return next;
      });
      setNodeCounts((prev) => {
        const next = { ...prev };
        toRemove.forEach((id) => delete next[id]);
        return next;
      });
      setExpandedNodeIds((prev) => {
        const next = new Set(prev);
        toRemove.forEach((id) => next.delete(id));
        keep.forEach((id) => next.delete(id));
        if (direction === 'inputs' && keep.size === 0) next.delete(nodeId);
        return next;
      });
      setExpandedOutputNodeIds((prev) => {
        const next = new Set(prev);
        toRemove.forEach((id) => next.delete(id));
        keep.forEach((id) => next.delete(id));
        if (direction === 'outputs' && keep.size === 0) next.delete(nodeId);
        return next;
      });
    },
    [expandedNodeIds, expandedOutputNodeIds, setEdgesSynced, setNodesSynced]
  );

  const collapseNode = useCallback(
    (nodeId: string, activeNodeId?: string | null) => collapseExpansion(nodeId, 'inputs', activeNodeId),
    [collapseExpansion]
  );

  const expandOutputs = useCallback(
    async (nodeId: string) => {
      const key = `${nodeId}_outputs`;
      if (expandedOutputNodeIds.has(nodeId) || inFlightExpansions.current.has(key)) return;
      inFlightExpansions.current.add(key);
      const stamp = expansionStamp(key);
      const datasetId = nodeId.split('#')[0];
      const needsFetch = !neighborhoodCache.current[datasetId];
      if (needsFetch) {
        pendingFetches.current += 1;
        setIsExpanding(true);
      }
      try {
        const neighborhood = await getOrFetchNeighborhood(datasetId);
        if (!neighborhood) return;
        if (stamp !== expansionStamp(key)) return;

        setNodeCounts((prev) => ({
          ...prev,
          [nodeId]: { inputs: neighborhood.inputs.total, outputs: neighborhood.outputs.total },
        }));
        applyPage(nodeId, 'outputs', neighborhood.outputs);
        setExpandedOutputNodeIds((prev) => new Set([...prev, nodeId]));
      } finally {
        inFlightExpansions.current.delete(key);
        if (needsFetch) {
          pendingFetches.current -= 1;
          if (pendingFetches.current === 0) setIsExpanding(false);
        }
      }
    },
    [applyPage, expandedOutputNodeIds, expansionStamp, getOrFetchNeighborhood]
  );

  const collapseOutputs = useCallback(
    (nodeId: string, activeNodeId?: string | null) => collapseExpansion(nodeId, 'outputs', activeNodeId),
    [collapseExpansion]
  );

  const showMore = useCallback(
    async (nodeId: string, direction: 'inputs' | 'outputs') => {
      const key = `${nodeId}_${direction}`;
      if (inFlightExpansions.current.has(key)) return;

      const datasetId = nodeId.split('#')[0];
      const cached = neighborhoodCache.current[datasetId];
      if (!cached) return;

      const offset = fetchedCounts.current[key] ?? 0;
      if (offset >= (serverTotals.current[key] ?? cached[direction].total)) return;

      inFlightExpansions.current.add(key);
      const stamp = expansionStamp(key);
      try {
        const page = await fetchNeighborhood(datasetId, version, system_model, pageParams(direction, offset));
        if (stamp !== expansionStamp(key)) return;
        applyPage(nodeId, direction, page[direction]);
      } catch {
        if (!toast.isActive(SHOW_MORE_ERROR_TOAST.id)) toast(SHOW_MORE_ERROR_TOAST);
      } finally {
        inFlightExpansions.current.delete(key);
      }
    },
    [applyPage, expansionStamp, system_model, toast, version]
  );

  const showMoreInputs = useCallback((nodeId: string) => showMore(nodeId, 'inputs'), [showMore]);

  const showMoreOutputs = useCallback((nodeId: string) => showMore(nodeId, 'outputs'), [showMore]);

  const collapseAll = useCallback(() => {
    // Bumped on the click, not in the reset effect: a page can resolve between the two.
    generation.current += 1;
    initialized.current = false;
    setResetKey((k) => k + 1);
  }, []);

  return {
    nodes,
    edges,
    nodeCounts,
    shownCounts,
    isLoading,
    isExpanding,
    rootNodeId,
    expandedNodeIds,
    expandNode,
    collapseNode,
    expandedOutputNodeIds,
    expandOutputs,
    collapseOutputs,
    collapseAll,
    showMoreInputs,
    showMoreOutputs,
  };
};
